import React, { useState, useEffect } from "react";
import { Line, Pie } from "react-chartjs-2";
import PercentIcon from "@mui/icons-material/Percent";
import Slider from "@mui/material/Slider";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../Redux/store";
import { IconButton, Typography } from "@mui/material";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import SIPPDFGenerator from "../pdf/pdf-sipcalc";
import SaveTitleDialog from "../dialog-box/SaveTitleDialog";
import { saveSIP, updateSIP } from "../Database/systematic-investment-plan";
import ReactGA from 'react-ga4'
import BootstrapTooltip from "../components/tooltip";
import { SIP } from "../models/sip";
import { useLocation } from "react-router-dom";
import { setCurrencySymbol } from "../Redux/currencySlice";
import SavedSnackbar from "../utils/saved-snackbar";


const SystematicInvestmentPlanCalculator: React.FC<{ sip?: SIP }> = ({ }) => {
  const location = useLocation();
  const [sipData, setsipData] = useState<SIP | null>(null);

  const MAX_MONTHLY_INVESTMENT = 10000000;
  const MAX_RATE = 30;
  const MAX_PERIOD = 50;
  const currencySymbol = useSelector((state: RootState) => state.country.currencySymbol);
  const [monthlyInvestment, setMonthlyInvestment] = useState<string>("50,000");
  const [rate, setRate] = useState<string>("10");
  const [period, setPeriod] = useState<string>("10");
  const [totalAmount, setTotalAmount] = useState<number | null>(null);
  const [interestEarned, setInterestEarned] = useState<number | null>(null);
  const [expectedAnnualReturn, setExpectedAnnualReturn] = useState<number | null>(null);
  const [monthlyDepositAmount, setMonthlyDepositAmount] = useState<number | null>(null);
  const [totalInvestment, setTotalInvestment] = useState<number | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [chartTotalAmount, setChartTotalAmount] = useState<number[]>([]);
  const [chartTotalInterest, setChartTotalInterest] = useState<number[]>([]);
  const [showPdfButton, setShowPdfButton] = useState(false);
  const [title, setTitle] = useState("");
  const dispatch = useDispatch();
  const [, setSymbol] = useState(currencySymbol);
  const [icon, setIcon] = useState<React.ReactNode>(<FavoriteBorderIcon />);
  const [errors, setErrors] = useState({
    monthlyInvestment: "",
    rate: "",
    period: "",
  });
  const [savedTitle, setSavedTitle] = useState("");
  const [xAxisLabels, setXAxisLabels] = useState<string[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [isCalculated, setIsCalculated] = useState(false);


  const handleReset = () => {
    ReactGA.event({
      category: 'SIP calculator',
      action: 'SIP Reset Button',
      label: 'SIP Reset',
    });

    setMonthlyInvestment("50,000");
    setRate("10");
    setPeriod("10");
    setTotalAmount(null);
    setInterestEarned(null);
    setExpectedAnnualReturn(null);
    setTotalInvestment(null);
    setShowPdfButton(false);
    setErrors({
      monthlyInvestment: "",
      rate: "",
      period: "",
    });
  };

  useEffect(() => {
    const { sip: passedSip, currencySymbol: passedCurrencySymbol } =
      location.state || {};

    if (passedCurrencySymbol) {
      dispatch(setCurrencySymbol(passedCurrencySymbol));
      setSymbol(passedCurrencySymbol);
    } else {
      setSymbol(currencySymbol);
    }

    if (passedSip) {
      setsipData(passedSip);
      setIcon(<FavoriteIcon style={{ color: "red" }} />);
      setMonthlyInvestment(passedSip.monthlyInvestment.toString());
      setRate(passedSip.rate.toString());
      setPeriod(passedSip.period.toString());
      setTitle(passedSip.title);
      setSavedTitle(passedSip.title || "");
      handleCalculate();
    }
    handleCalculate();

  }, [location.state, , sipData]);


  const handleSubmit = async () => {
    if (!validateInputs()) {
      return;
    }

    const sipUpdate: SIP = {
      monthlyInvestment: parseFloat(monthlyInvestment.replace(/,/g, "")),
      rate: parseFloat(rate),
      period: parseInt(period?.toString() || '0', 10),
      totalAmount: totalAmount ?? 0,
      interestEarned: interestEarned ?? 0,
      currencySymbol,
      title: title,
    };

    if (sipData?.id) {
      await updateSIP(sipData.id, sipUpdate);
      console.log(`emi with ID ${sipData.id} updated successfully.`);
    } else {
      await saveSIP(sipUpdate);
      console.log("New emi created successfully.");
    }
    setSnackbarMessage("Saved successfully!");
    setSnackbarOpen(true);
    setIcon(<FavoriteIcon style={{ color: "red" }} />);
    handleDialogClose();
    setTitle("");

    handleDialogClose();
    setTitle("");

  }

  const validateInputs = () => {
    const newErrors = {
      monthlyInvestment: "",
      rate: "",
      period: "",
    };

    let isValid = true;
    const minDepositAmount = 500;
    const minRate = 1;
    const maxRate = 30;
    const minPeriod = 1;
    const maxPeriod = 30;

    if (!monthlyInvestment || isNaN(parseFloat(monthlyInvestment.replace(/,/g, "")))) {
      newErrors.monthlyInvestment = "Monthly deposit amount is mandatory.";
      isValid = false;
    } else {
      const depositValue = parseFloat(monthlyInvestment.replace(/,/g, ""));
      if (depositValue < minDepositAmount) {
        newErrors.monthlyInvestment = `Deposit amount can't be less than ${currencySymbol}${minDepositAmount}.`;
        isValid = false;
      }
    }

    if (!rate || isNaN(parseFloat(rate))) {
      newErrors.rate = "Please fill in the Expected Annual Return Rate.";
      isValid = false;
    } else {
      const rateValue = parseFloat(rate);
      if (rateValue < minRate || rateValue > maxRate) {
        newErrors.rate = `Interest rate must be between ${minRate}% and ${maxRate}%.`;
        isValid = false;
      }
    }

    if (!period || isNaN(parseFloat(period))) {
      newErrors.period = "Please fill in the Investment Period.";
      isValid = false;
    } else {
      const periodValue = parseFloat(period);
      if (periodValue < minPeriod || periodValue > maxPeriod) {
        newErrors.period = `Investment period must be between ${minPeriod} and ${maxPeriod} years.`;
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };
  const handleCalculate = async () => {
    ReactGA.event({
      category: 'SIP calculator',
      action: 'SIP Calculate Button',
      label: 'SIP Calculator'
    });

    if (!validateInputs()) {
      console.log("Validation failed");
      return;
    }
    const labels = Array.from(
      { length: parseInt(period) },
      (_, index) => `Year ${index + 1}`
    );
    setXAxisLabels(labels);

    const monthlyInvestmentValue =
      parseFloat(monthlyInvestment.replace(/,/g, "")) || 0;
    const annualRateValue = parseFloat(rate) / 100;
    const monthlyRateValue = annualRateValue / 12;
    const totalMonths = parseInt(period) * 12;

    const futureValue =
      monthlyInvestmentValue *
      ((Math.pow(1 + monthlyRateValue, totalMonths) - 1) / monthlyRateValue) *
      (1 + monthlyRateValue);

    const totalContributions = monthlyInvestmentValue * totalMonths;
    const interest = futureValue - totalContributions;

    setTotalAmount(futureValue);
    setInterestEarned(interest);
    setExpectedAnnualReturn(interest);
    setTotalInvestment(totalContributions);
    setMonthlyDepositAmount(monthlyInvestmentValue);
    setIsCalculated(true)


    const totalAmountData = Array.from(
      { length: parseInt(period) },
      (_, index) => {
        const yearsValue = index + 1;
        const monthlyInvestmentValue =
          parseFloat(monthlyInvestment.replace(/,/g, "")) || 0;
        const rateValue = parseFloat(rate) / 100 / 12;

        const futureValue =
          monthlyInvestmentValue *
          (((Math.pow(1 + rateValue, yearsValue * 12) - 1) / rateValue) *
            (1 + rateValue));

        return futureValue;
      }
    );

    const totalInterestData = totalAmountData.map((amount, index) => {
      const yearsValue = index + 1;
      const monthlyInvestmentValue =
        parseFloat(monthlyInvestment.replace(/,/g, "")) || 0;
      const totalContributions = monthlyInvestmentValue * yearsValue * 12;

      return amount - totalContributions;
    });

    setChartTotalAmount(totalAmountData);
    setChartTotalInterest(totalInterestData);
    setShowPdfButton(true);

  };

  const handleMonthlyInvestmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.replace(/,/g, "");

    if (value === "" || /^\d*\.?\d*$/.test(value)) {
      const numberValue = value === "" ? null : parseFloat(value);

      if (numberValue === null || (numberValue > 0 && numberValue <= MAX_MONTHLY_INVESTMENT)) {
        setMonthlyInvestment(
          value === ""
            ? ""
            : new Intl.NumberFormat("en-IN", {
              maximumFractionDigits: 0,
            }).format(numberValue as number)
        );
      }
    }
  };


  const handleNumberInput = (
    e: React.ChangeEvent<HTMLInputElement>,
    setter: React.Dispatch<React.SetStateAction<string>>,
    maxLimit: number,
    decimalLimit: number = 2
  ) => {
    const value = e.target.value;
    const validValue = value.replace(/[^0-9.]/g, "");

    const parts = validValue.split(".");
    if (parts.length > 1 && parts[1].length > decimalLimit) {
      parts[1] = parts[1].slice(0, decimalLimit);
    }

    const formattedValue = parts.join(".");

    if (formattedValue === "" || (parseFloat(formattedValue) > 0 && parseFloat(formattedValue) <= maxLimit)) {
      setter(formattedValue);
    }
  };

  const handlePeriodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      const numberValue = value === "" ? "" : parseInt(value);

      if (numberValue === "" || (numberValue > 0 && numberValue <= MAX_PERIOD)) {
        setPeriod(value);
      }
    }
  };

  // const xAxisLabels = Array.from({ length: MAX_PERIOD }, (_, index) => `Year ${index+1}`);

  const LineChartData = {
    labels: xAxisLabels,
    datasets: [
      {
        label: "Total Amount",
        data: chartTotalAmount,
        borderColor: "#42A5F5",
        backgroundColor: "rgba(66, 165, 245, 0.2)",
      },
      {
        label: "Total Interest",
        data: chartTotalInterest,
        borderColor: "#FFA726",
        backgroundColor: "rgba(255, 167, 38, 0.2)",
      },
    ],
  };


  const handleDialogOpen = () => {
    setDialogOpen(true);
  };
  const handleDialogClose = () => {
    setDialogOpen(false);
    setTitle("");
  };

  const handleCancel = () => {
    handleDialogClose();
  };
  const handleSnackbarClose = (
    event: React.SyntheticEvent | Event,
    reason: "timeout" | "clickaway" | "escapeKeyDown"
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleCalculate();
    }
  };


  return (
    <>
      <Helmet>
        <title>SIP Calculator</title>
        <meta
          name="description"
          content="Use our SIP Calculator to estimate the future value of your investments. Calculate returns based on monthly contributions, interest rate, and investment duration."
        />
        <link rel="canonical" href="/sip-calculator.html" />
      </Helmet>
      <SavedSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        onClose={handleSnackbarClose}
      />
      <div className="container mt-5" onKeyPress={handleKeyPress}>
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-2" style={{ fontFamily: "serif", fontSize: "30px", fontWeight: "bold" }}>
            SIP Calculator
          </h5>
        </div>
        <p>
          A SIP Calculator helps estimate the future value of a series of periodic investments over a specific period,
          considering the expected rate of return.
        </p>
        <div className="row justify-content-center">
          {/* {savedTitle && (
            <Typography variant="h6" style={{ marginLeft: '20px' }}>
              {savedTitle}
            </Typography>
          )} */}
          <div className="col-md-6">

            <div className="card shadow-sm" style={{ width: '100%', height: '585px', margin: '0 auto' }}>
              <div className="card-body">

                <div className="mb-3">
                  <label htmlFor="monthlyInvestment" className="form-label">
                    Monthly Investment ({currencySymbol})
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.monthlyInvestment ? "is-invalid" : ""}`}
                    id="monthlyInvestment"
                    value={monthlyInvestment}
                    onChange={handleMonthlyInvestmentChange}
                    max={MAX_MONTHLY_INVESTMENT}
                  />
                  {errors.monthlyInvestment && (
                    <div className="invalid-feedback">
                      {errors.monthlyInvestment}
                    </div>
                  )}
                </div>
                <div className="mb-3 d-flex justify-content-center">
                  <Slider
                    min={500}
                    max={10000000}
                    step={1000}
                    value={parseFloat(monthlyInvestment.replace(/,/g, ""))}
                    onChange={(e, newValue) =>
                      setMonthlyInvestment(newValue.toString())
                    }
                    valueLabelDisplay="auto"
                    shiftStep={30}
                    sx={{ width: "95%" }}
                  />
                </div>

                <div className="mb-3 mt-3">
                  <label htmlFor="rate" className="form-label">
                    Expected Annual Return Rate (%)
                  </label>
                  <div className="input-group">
                    <input
                      type="text"
                      className={`form-control ${errors.rate ? "is-invalid" : ""}`}
                      id="rate"
                      value={rate}
                      onChange={(e) => handleNumberInput(e, setRate, 30, 2)}
                    />
                    <span className="input-group-text">
                      <PercentIcon />
                    </span>
                    {errors.rate && (
                      <div className="invalid-feedback">{errors.rate}</div>
                    )}
                  </div>
                </div>

                <div className="mb-3 mt-3" >
                  <label htmlFor="period" className="form-label">
                    Investment Period (Years)
                  </label>
                  <input
                    type="text"
                    className={`form-control ${errors.period ? "is-invalid" : ""}`}
                    id="period"
                    value={period}
                    onChange={handlePeriodChange}
                    max={MAX_PERIOD}
                  />
                  {errors.period && (
                    <div className="invalid-feedback">{errors.period}</div>
                  )}
                </div>

                <div className="d-flex justify-content-center " style={{ marginTop: '60px' }}>
                  <button
                    className="btn btn-primary btn-lg "
                    onClick={handleCalculate}

                  >
                    Calculate
                  </button>
                </div>
                <div className='d-flex justify-content-center'>
                  <a
                    onClick={handleReset}
                    style={{ cursor: "pointer", textDecoration: "none" }}
                    className=" pt-2"
                  >
                    Reset
                  </a>
                </div>
              </div>
            </div>
          </div>
          {totalInvestment !== null &&
            interestEarned !== null &&
            totalAmount != null && (
              <div className="col-md-6">
                <div className="card mb-3 mt-4 mt-md-0">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center">

                      <h5 className="card-title ">Result</h5>
                      <BootstrapTooltip title="Save Calculation" placement="top">

                        <IconButton color="secondary" onClick={handleDialogOpen}>
                          {icon}
                        </IconButton>
                      </BootstrapTooltip>
                    </div>
                    <p className="card-text">
                      <strong>Monthly Deposit Amount:</strong>
                      <span className="float-end">
                        {currencySymbol}{" "}
                        {monthlyDepositAmount !== null
                          ? `${new Intl.NumberFormat("en-IN").format(Math.ceil(Number(monthlyDepositAmount.toFixed(2))))}`
                          : '0'}
                      </span>
                    </p>
                    <p className="card-text">
                      <strong>Total Investment:</strong>{" "}
                      <span className="float-end">
                        {" "}
                        {currencySymbol}{" "}
                        {totalInvestment !== null
                          ? `${new Intl.NumberFormat("en-IN").format(Math.ceil(
                            totalInvestment)
                          )}`
                          : ""}
                      </span>
                    </p>

                    <p className="card-text">
                      <strong>Total Interest Earned:</strong>{" "}
                      <span className="float-end">
                        {" "}
                        {currencySymbol}{" "}
                        {interestEarned !== null
                          ? `${new Intl.NumberFormat("en-IN").format(Math.ceil(
                            interestEarned)
                          )}`
                          : ""}
                      </span>
                    </p>

                    <p className="card-text">
                      <strong>Total Amount at Maturity:</strong>{" "}
                      <span className="float-end">
                        {" "}
                        {currencySymbol}{" "}
                        {totalAmount !== null
                          ? `${new Intl.NumberFormat("en-IN").format(Math.ceil(
                            totalAmount)
                          )}`
                          : ""}
                      </span>
                    </p>
                  </div>
                </div>

                {totalAmount !== null && interestEarned !== null && (
                  <div className="card mb-3">
                    <div className="card-body">
                      <h5 className="card-title mb-4">Deposit Breakdown</h5>
                      <div
                        style={{ width: "100%", height: "270px" }}
                        className="d-flex justify-content-center align-items-center"
                      >
                        <Line data={LineChartData} />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          <div className="d-flex justify-content-end" style={{ marginTop: "50px", paddingLeft: "90px" }}>

            {showPdfButton && (
              <SIPPDFGenerator
                monthlyInvestment={monthlyInvestment}
                rate={rate}
                period={period}
                totalInvestment={totalInvestment}
                interestEarned={interestEarned}
                totalAmount={totalAmount}
                chartTotalAmount={chartTotalAmount}
                chartTotalInterest={chartTotalInterest}
                currencySymbol={currencySymbol}
                isCalculated={isCalculated}
              />
            )}
          </div>
        </div>
        <SaveTitleDialog
          open={dialogOpen}
          onClose={handleDialogClose}
          title={title}
          setTitle={setTitle}
          onSubmit={handleSubmit}
          onCancel={handleCancel}


        />
      </div>
    </>
  );
};

export default SystematicInvestmentPlanCalculator;
